import AddRounded from '@mui/icons-material/AddRounded';
import { styled } from '@mui/system';
import { IconButton } from '@src/common/components/IconButton';
import { Paper } from '@src/common/components/Paper';
import { Stack } from '@src/common/components/Stack';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { useMeasurement } from '@src/common/measurements/useMeasurement';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { NoAxleInformation } from '@src/orders/PermitFormSections/axlesConfiguration/NoAxleInformation';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { axleRowFields } from '@src/orders/schemas/AxleInformationInputsSchema';
import {
  AxleCapacityField,
  AxleFormName,
  AxleGroupField,
  AxleSpacingField,
  AxleTypeField,
  AxleWeightField,
  AxleWidthField,
  IsSteeringAxleField,
  SuspensionTypeField,
  TireCountField,
  TireRatingField,
  TireSizeField,
} from '@src/permitField/AxleInformationFields';
import type { ReactNode } from 'react';
import type { SetFieldValue, UseFormGetValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TableProps = {
  name: string;
  children: ReactNode;
  /*oxlint-disable*/
  setValue: SetFieldValue<any>;
  getValue: UseFormGetValues<any>;
  /*oxlint-enable*/
  axleCount: number;
  vehiclePrefix?: string;
  fields?: string[];
  missingAxlesError?: boolean;
  addAxleDisabled?: boolean;
};

export const AxlesTable = ({
  name,
  children,
  fields,
  axleCount,
  setValue,
  getValue,
  vehiclePrefix,
  missingAxlesError,
  addAxleDisabled,
}: TableProps) => {
  const { t } = useTranslation();

  const { isMetric } = useMeasurement();
  const measurementUnit = isMetric
    ? t('units.meters')
    : t('units.feetAndInches');
  const weightUnit = isMetric ? t('units.kilos') : t('units.pounds');
  const pathToAxles = vehiclePrefix
    ? `${vehiclePrefix}.${AxleFormName}`
    : AxleFormName;

  const addAxle = () => {
    const currentAxles = getValue(pathToAxles);
    const emptyAxle = generateEmptyAxle(fields);

    if (Array.isArray(currentAxles)) {
      currentAxles.push(emptyAxle);
      setValue(pathToAxles, currentAxles);
    } else {
      setValue(pathToAxles, [emptyAxle]);
    }
  };
  return (
    <>
      {axleCount === 0 && (
        <>
          <NoAxleInformation missingAxlesError={missingAxlesError} />
          <Stack maxWidth="fit-content">
            <AddAxleButton onClick={addAxle} />
          </Stack>
        </>
      )}
      {axleCount > 0 && (
        <Paper>
          <Table aria-label={name} sx={{ minWidth: 650 }} padding="none">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('axleNumberHeader')}
                </StyledTableCell>
                <ConditionalField
                  conditions={AxleTypeField.fieldName}
                  fields={fields}
                >
                  <StyledTableCell>
                    {t(`fields.${AxleTypeField.fieldName}`)}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={AxleWeightField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${AxleWeightField.fieldName}`) +
                      ` (${weightUnit})`}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={AxleSpacingField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${AxleSpacingField.fieldName}`) +
                      ` (${measurementUnit})`}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={TireCountField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${TireCountField.fieldName}`)}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={TireSizeField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${TireSizeField.fieldName}`)}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={TireRatingField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${TireRatingField.fieldName}`) +
                      ` (${weightUnit})`}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={AxleCapacityField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${AxleCapacityField.fieldName}`) +
                      ` (${weightUnit})`}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={AxleWidthField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${AxleWidthField.fieldName}`) +
                      ` (${measurementUnit})`}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={AxleGroupField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${AxleGroupField.fieldName}`)}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={SuspensionTypeField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${SuspensionTypeField.fieldName}`)}
                  </StyledTableCell>
                </ConditionalField>
                <ConditionalField
                  fields={fields}
                  conditions={IsSteeringAxleField.fieldName}
                >
                  <StyledTableCell>
                    {t(`fields.${IsSteeringAxleField.fieldName}`)}
                  </StyledTableCell>
                </ConditionalField>

                <StyledTableCell />
              </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
            <TableFooter>
              <TableRow>
                <StyledTableCell>
                  <AddAxleButton onClick={addAxle} disabled={addAxleDisabled} />
                </StyledTableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </Paper>
      )}
    </>
  );
};

const AddAxleButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <IconButton
      aria-label={t('addAxle')}
      onClick={onClick}
      className={AddButtonStyle}
      disabled={disabled}
    >
      <AddRounded style={{ margin: 0 }} />
    </IconButton>
  );
};

const AddButtonStyle = css({
  backgroundColor: theme.palette.secondary.secondaryContainer,
  color: theme.palette.secondary.onSecondaryContainer,
  margin: theme.spacing['1'],
});

const generateEmptyAxle = (fields: string[] | undefined) => {
  const axle: Record<string, null> = {};
  Object.keys(axleRowFields).forEach((field) => {
    if (fields?.includes(field)) {
      axle[field] = null;
    }
  });
  return axle;
};

const StyledTableCell = styled(TableCell)`
  padding: ${theme.spacing['2']} ${theme.spacing['1']};
`;
